// ===========================================
// APP SLICE - Global App State
// ===========================================

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LoadingState } from '@/types';

interface AppState {
  // App initialization
  initialized: boolean;
  initStatus: LoadingState;
  initError: string | null;

  // Connection status
  isOnline: boolean;

  // App version
  version: string;
  updateAvailable: boolean;

  // Feature flags (runtime)
  featureFlags: Record<string, boolean>;
}

const initialState: AppState = {
  initialized: false,
  initStatus: 'idle',
  initError: null,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  version: '1.0.0',
  updateAvailable: false,
  featureFlags: {},
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // Initialization
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
      state.initStatus = action.payload ? 'succeeded' : 'idle';
    },
    setInitStatus: (state, action: PayloadAction<LoadingState>) => {
      state.initStatus = action.payload;
    },
    setInitError: (state, action: PayloadAction<string | null>) => {
      state.initError = action.payload;
      state.initStatus = action.payload ? 'failed' : 'idle';
    },

    // Connection
    setOnlineStatus: (state, action: PayloadAction<boolean>) => {
      state.isOnline = action.payload;
    },

    // Updates
    setUpdateAvailable: (state, action: PayloadAction<boolean>) => {
      state.updateAvailable = action.payload;
    },
    setVersion: (state, action: PayloadAction<string>) => {
      state.version = action.payload;
    },

    // Feature flags
    setFeatureFlag: (state, action: PayloadAction<{ flag: string; enabled: boolean }>) => {
      state.featureFlags[action.payload.flag] = action.payload.enabled;
    },
    setFeatureFlags: (state, action: PayloadAction<Record<string, boolean>>) => {
      state.featureFlags = { ...state.featureFlags, ...action.payload };
    },
  },
});

export const {
  setInitialized,
  setInitStatus,
  setInitError,
  setOnlineStatus,
  setUpdateAvailable,
  setVersion,
  setFeatureFlag,
  setFeatureFlags,
} = appSlice.actions;

export const appReducer = appSlice.reducer;

// Selectors
export const selectInitialized = (state: { app: AppState }) => state.app.initialized;
export const selectInitStatus = (state: { app: AppState }) => state.app.initStatus;
export const selectInitError = (state: { app: AppState }) => state.app.initError;
export const selectIsOnline = (state: { app: AppState }) => state.app.isOnline;
export const selectUpdateAvailable = (state: { app: AppState }) => state.app.updateAvailable;
export const selectVersion = (state: { app: AppState }) => state.app.version;
export const selectFeatureFlag = (flag: string) => (state: { app: AppState }) =>
  state.app.featureFlags[flag] ?? false;
